import { useEffect, useRef, useState } from 'react';

export function useDebouncedValue<T>(val: T, delayMs = 500, callback?: (val: T) => void): T {
  const [debouncedVal, setDebouncedVal] = useState(val);
  const timeout = useRef<number | null>(null);
  const deferredCallbackTimeout = useRef<number | null>(null);

  useEffect(() => {
    if (timeout.current !== null) {
      window.clearTimeout(timeout.current);
    }
    if (deferredCallbackTimeout.current !== null) {
      window.clearTimeout(deferredCallbackTimeout.current);
    }

    timeout.current = window.setTimeout(() => {
      timeout.current = null;
      setDebouncedVal((prev) => {
        if (prev === val) {
          return prev;
        }
        // Never call `callback` inside this updater: it may sync URL / router or
        // call parent setState, which must not run while React applies this update.
        deferredCallbackTimeout.current = window.setTimeout(() => {
          deferredCallbackTimeout.current = null;
          callback?.(val);
        }, 0);
        return val;
      });
    }, delayMs);

    return () => {
      if (timeout.current !== null) {
        window.clearTimeout(timeout.current);
        timeout.current = null;
      }
      if (deferredCallbackTimeout.current !== null) {
        window.clearTimeout(deferredCallbackTimeout.current);
        deferredCallbackTimeout.current = null;
      }
    };
  }, [val, delayMs, callback]);

  return debouncedVal;
}